import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { authApi } from '@/api/auth.api';
import { formatApiMessage } from '@/api/client';
import { usersApi } from '@/api/users.api';
import { AppShell } from '@/components/layout/AppShell';
import { PdfSignatureExtractor } from '@/components/sign/PdfSignatureExtractor';
import { SignaturePad } from '@/components/sign/SignaturePad';
import { downscaleDataUrl } from '@/lib/image';
import { useAuthStore } from '@/stores/auth.store';

export function SignaturePage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);
  const [pendingSignature, setPendingSignature] = useState<string>('');
  const [pendingInitial, setPendingInitial] = useState<string>('');

  const { data: me } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => authApi.me(),
  });

  const save = useMutation({
    mutationFn: async (input: { signatureImageUrl?: string | null; initialImageUrl?: string | null }) => {
      // Downscale before send — PDF-extracted signatures or full-resolution
      // canvases can be 500KB+. 600px wide is plenty for embedding in PDFs
      // and keeps the saved data URL under ~150KB.
      const scaled = {
        signatureImageUrl:
          input.signatureImageUrl == null
            ? input.signatureImageUrl
            : await downscaleDataUrl(input.signatureImageUrl, 600),
        initialImageUrl:
          input.initialImageUrl == null
            ? input.initialImageUrl
            : await downscaleDataUrl(input.initialImageUrl, 300),
      };
      return usersApi.updateMySignature(scaled);
    },
    onSuccess: async () => {
      toast.success(t('settings_signature.saved_hint'));
      const fresh = await authApi.me();
      if (fresh) setUser(fresh);
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      setPendingSignature('');
      setPendingInitial('');
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const currentSignature = me?.signatureImageUrl ?? null;
  const currentInitial = me?.initialImageUrl ?? null;

  return (
    <AppShell>
      <div className="mb-6">
        <h1 className="page-title">{t('settings_signature.title')}</h1>
        <p className="page-subtitle">{t('settings_signature.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SignatureBlock
          title={t('settings_signature.full_signature')}
          kind="signature"
          current={currentSignature}
          pending={pendingSignature}
          onChange={setPendingSignature}
          onSave={() =>
            save.mutate({ signatureImageUrl: pendingSignature || null })
          }
          onClear={() => save.mutate({ signatureImageUrl: null })}
          saving={save.isPending}
        />
        <SignatureBlock
          title={t('settings_signature.initials')}
          kind="initial"
          current={currentInitial}
          pending={pendingInitial}
          onChange={setPendingInitial}
          onSave={() =>
            save.mutate({ initialImageUrl: pendingInitial || null })
          }
          onClear={() => save.mutate({ initialImageUrl: null })}
          saving={save.isPending}
        />
      </div>

      <div className="card !p-4 mt-5 bg-info-bg border-info text-[13px] text-info-fg">
        <strong>{t('settings_signature.note')}:</strong> {t('settings_signature.note_text')}
      </div>
    </AppShell>
  );
}

function SignatureBlock({
  title,
  kind,
  current,
  pending,
  onChange,
  onSave,
  onClear,
  saving,
}: {
  title: string;
  kind: 'signature' | 'initial';
  current: string | null;
  pending: string;
  onChange: (v: string) => void;
  onSave: () => void;
  onClear: () => void;
  saving: boolean;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'draw' | 'font' | 'upload' | 'pdf'>('draw');
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleFile(file: File) {
    if (!file.type.startsWith('image/')) {
      toast.error(t('settings_signature.image_only'));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('settings_signature.file_too_large'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result ?? '');
      if (dataUrl.startsWith('data:image/')) onChange(dataUrl);
    };
    reader.readAsDataURL(file);
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-900 m-0">{title}</h3>
        {current && (
          <button
            onClick={() => {
              if (confirm(t(`settings_signature.remove_${kind}_confirm`))) onClear();
            }}
            className="btn btn-ghost btn-sm text-danger"
            disabled={saving}
          >
            {t('common.remove')}
          </button>
        )}
      </div>

      {current ? (
        <div className="mb-3">
          <div className="text-[11px] uppercase font-semibold text-muted tracking-wide mb-1">
            {t('settings_signature.current')}
          </div>
          <div className="border border-border rounded-md p-2 bg-gray-50 inline-block max-w-full">
            <img src={current} alt="" className="max-h-24 max-w-full" />
          </div>
        </div>
      ) : (
        <div className="text-[12px] text-muted mb-3">{t(`settings_signature.no_${kind}`)}</div>
      )}

      <div className="flex gap-1 mb-2 flex-wrap">
        <button
          type="button"
          onClick={() => setMode('draw')}
          className={`px-3 py-1 rounded-md text-[12px] font-medium ${
            mode === 'draw' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700'
          }`}
        >
          {t('settings_signature.tab_draw')}
        </button>
        <button
          type="button"
          onClick={() => setMode('font')}
          className={`px-3 py-1 rounded-md text-[12px] font-medium ${
            mode === 'font' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700'
          }`}
        >
          {t('settings_signature.tab_font')}
        </button>
        <button
          type="button"
          onClick={() => setMode('upload')}
          className={`px-3 py-1 rounded-md text-[12px] font-medium ${
            mode === 'upload' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700'
          }`}
        >
          {t('settings_signature.tab_upload')}
        </button>
        <button
          type="button"
          onClick={() => setMode('pdf')}
          className={`px-3 py-1 rounded-md text-[12px] font-medium ${
            mode === 'pdf' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700'
          }`}
        >
          {t('settings_signature.tab_pdf')}
        </button>
      </div>

      {mode === 'draw' && <SignaturePad onChange={onChange} />}

      {mode === 'font' && <FontSignature kind={kind} onChange={onChange} />}

      {mode === 'upload' && (
        <div
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-border-strong rounded-md p-6 text-center cursor-pointer hover:border-primary hover:bg-primary-50 transition-colors"
        >
          {pending ? (
            <img src={pending} alt="" className="max-h-24 mx-auto" />
          ) : (
            <>
              <div className="text-3xl mb-2">🖼️</div>
              <div className="text-[13px] font-medium text-gray-900">{t('settings_signature.upload_drop')}</div>
              <div className="text-[11px] text-muted mt-1">{t('settings_signature.upload_hint')}</div>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
          />
        </div>
      )}

      {mode === 'pdf' && <PdfSignatureExtractor onExtract={onChange} />}

      <div className="flex justify-end mt-3 pt-3 border-t border-border">
        <button
          onClick={onSave}
          disabled={!pending || saving}
          className="btn btn-primary"
        >
          {saving ? t('common.saving') : t(`settings_signature.save_${kind}`)}
        </button>
      </div>
    </div>
  );
}

/**
 * Renders the user's typed name in a handwriting-style web font, then
 * rasterises it to a PNG data URL so the signing pipeline gets the same
 * format as the drawn / uploaded variants. Customer asked for an "Azad
 * Yusifov" type look — we ship four cursive options to choose from.
 */
const FONT_CHOICES: Array<{ id: string; label: string; family: string; size: number }> = [
  { id: 'dancing',    label: 'Dancing Script', family: '"Dancing Script", cursive',   size: 56 },
  { id: 'great',      label: 'Great Vibes',    family: '"Great Vibes", cursive',      size: 64 },
  { id: 'sacramento', label: 'Sacramento',     family: '"Sacramento", cursive',       size: 60 },
  { id: 'caveat',     label: 'Caveat',         family: '"Caveat", cursive',           size: 64 },
];

const FONT_LINK_ID = 'esd-handwriting-fonts';
function ensureHandwritingFontsLoaded(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(FONT_LINK_ID)) return;
  const link = document.createElement('link');
  link.id = FONT_LINK_ID;
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&family=Dancing+Script:wght@400;700&family=Great+Vibes&family=Sacramento&display=swap';
  document.head.appendChild(link);
}

function FontSignature({
  kind,
  onChange,
}: {
  kind: 'signature' | 'initial';
  onChange: (dataUrl: string) => void;
}) {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.user);
  const defaultName =
    kind === 'initial'
      ? `${me?.firstName?.[0] ?? ''}${me?.lastName?.[0] ?? ''}`.toUpperCase()
      : `${me?.firstName ?? ''} ${me?.lastName ?? ''}`.trim();
  const [text, setText] = useState(defaultName);
  const [fontId, setFontId] = useState(FONT_CHOICES[0].id);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    ensureHandwritingFontsLoaded();
  }, []);

  const choice = FONT_CHOICES.find((f) => f.id === fontId) ?? FONT_CHOICES[0];

  // Re-render to canvas + emit PNG whenever inputs change. We give the font
  // a beat to load before drawing — otherwise the first paint falls back to
  // serif and the user gets an ugly preview.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      ctx.font = `${choice.size}px ${choice.family}`;
      ctx.fillStyle = '#111827';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text || ' ', w / 2, h / 2);
      onChange(canvas.toDataURL('image/png'));
    };

    if ('fonts' in document) {
      document.fonts.load(`${choice.size}px ${choice.family}`).then(draw).catch(draw);
    } else {
      draw();
    }
  }, [text, choice.family, choice.size, onChange]);

  return (
    <div>
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={60}
        placeholder={
          kind === 'initial'
            ? (t('settings_signature.font_initial_placeholder') as string)
            : (t('settings_signature.font_name_placeholder') as string)
        }
        className="input mb-2 text-[14px]"
      />
      <div className="flex flex-wrap gap-1.5 mb-2">
        {FONT_CHOICES.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFontId(f.id)}
            style={{ fontFamily: f.family, fontSize: 18 }}
            className={`px-3 py-1 rounded-md border ${
              fontId === f.id
                ? 'border-primary bg-primary-50 text-primary'
                : 'border-border bg-white text-gray-700'
            }`}
          >
            {text || f.label}
          </button>
        ))}
      </div>
      <canvas
        ref={canvasRef}
        width={500}
        height={120}
        className="border border-border rounded-md bg-white w-full"
        style={{ maxWidth: '100%' }}
      />
    </div>
  );
}
